import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
  Alert, Tooltip, Autocomplete, TablePagination, InputAdornment, MenuItem,
} from '@mui/material';
import { Add, Edit, Cancel, ContentCopy, UploadFile, Search, Send, WarningAmber } from '@mui/icons-material';
import { interviewScheduleService, userService } from '../services/api';
import { usePagination } from '../hooks/usePagination';
import { EMPLOYMENT_TYPE_META, HIRING_TYPES } from './internshipTypes';

// ─── Types ────────────────────────────────────────────────────────────────────

interface PanelMember {
  userId: number;
  fullName: string;
}

interface InterviewRow {
  id: number;
  candidateName: string;
  candidateEmail: string;
  candidatePhone: string | null;
  position: string;
  hiringType: string;
  resumeFileName: string | null;
  resumeUrl: string | null;
  scheduledAt: string;
  durationMinutes: number;
  status: string;
  cancelReason: string | null;
  inviteEmailSent: boolean;
  candidateJoinUrl: string;
  notes: string | null;
  panelMembers: PanelMember[];
  createdByName: string | null;
}

interface EmployeeOption {
  id: number;
  fullName: string;
  role: string;
}

interface FormState {
  candidateName: string;
  candidateEmail: string;
  candidatePhone: string;
  position: string;
  hiringType: string;
  scheduledAt: string; // datetime-local value
  durationMinutes: number;
  notes: string;
  panel: EmployeeOption[];
}

const emptyForm: FormState = {
  candidateName: '',
  candidateEmail: '',
  candidatePhone: '',
  position: '',
  hiringType: 'FullTime',
  scheduledAt: '',
  durationMinutes: 30,
  notes: '',
  panel: [],
};

const statusColor = (status: string): 'default' | 'success' | 'error' | 'warning' => {
  switch (status) {
    case 'Scheduled': return 'success';
    case 'Cancelled': return 'error';
    case 'Completed': return 'default';
    case 'NoShow': return 'warning';
    default: return 'default';
  }
};

// A "Scheduled" interview whose invite email never actually sent (Email Settings not
// configured/working) is displayed as "Pending" — the candidate hasn't been notified yet, even
// though the schedule itself is real. Every other status shows as-is; email delivery is only
// meaningful for an active, upcoming Scheduled interview.
const displayStatus = (row: Pick<InterviewRow, 'status' | 'inviteEmailSent'>) =>
  row.status === 'Scheduled' && !row.inviteEmailSent ? 'Pending' : row.status;
const displayStatusColor = (row: Pick<InterviewRow, 'status' | 'inviteEmailSent'>) =>
  row.status === 'Scheduled' && !row.inviteEmailSent ? 'warning' : statusColor(row.status);

// Formats a "YYYY-MM-DDTHH:mm:ss" (IST wall-clock, as returned by the API) for display —
// avoids `new Date(...)` re-interpreting it through the browser's local timezone.
const formatIst = (value: string) => {
  const [datePart, timePart] = value.split('T');
  if (!datePart || !timePart) return value;
  const [y, m, d] = datePart.split('-');
  const [hh, mm] = timePart.split(':');
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  const dateLabel = date.toLocaleDateString(undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  let hour = Number(hh);
  const suffix = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${dateLabel}, ${hour}:${mm} ${suffix} IST`;
};

// Same "YYYY-MM-DDTHH:mm" shape a datetime-local input needs, sliced straight off the API value
// (already IST wall-clock) — no timezone math.
const toDateTimeLocalValue = (value: string) => value.slice(0, 16);

const InterviewScheduling: React.FC = () => {
  const [interviews, setInterviews] = useState<InterviewRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hiringFilter, setHiringFilter] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const [cancelTarget, setCancelTarget] = useState<InterviewRow | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await interviewScheduleService.getAll({
        ...(search ? { search } : {}),
        ...(hiringFilter ? { hiringType: hiringFilter } : {}),
      });
      setInterviews(data);
    } catch {
      setError('Could not load interviews.');
    } finally {
      setLoading(false);
    }
  }, [search, hiringFilter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh so a status change (e.g. an invite email finally going through after Email
  // Settings gets fixed, or another admin scheduling/cancelling) shows up without a manual page
  // reload. Paused while the schedule/reschedule dialog or the cancel-confirm dialog is open, so
  // a background refresh never resets the form the admin is mid-typing in.
  useEffect(() => {
    if (formOpen || cancelTarget) return;
    const interval = setInterval(() => { load(); }, 15000);
    return () => clearInterval(interval);
  }, [load, formOpen, cancelTarget]);

  useEffect(() => {
    userService.getEmployees().then((data: any[]) =>
      setEmployees(data.map(u => ({ id: u.id, fullName: u.fullName, role: u.role })))
    ).catch(() => {});
  }, []);

  const { pagedRows, paginationProps } = usePagination(interviews, 10);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setResumeFile(null);
    setFormOpen(true);
  };

  const openReschedule = (row: InterviewRow) => {
    setEditingId(row.id);
    setForm({
      candidateName: row.candidateName,
      candidateEmail: row.candidateEmail,
      candidatePhone: row.candidatePhone || '',
      position: row.position,
      hiringType: row.hiringType || 'FullTime',
      scheduledAt: toDateTimeLocalValue(row.scheduledAt),
      durationMinutes: row.durationMinutes,
      notes: row.notes || '',
      panel: row.panelMembers.map(p => ({ id: p.userId, fullName: p.fullName, role: '' })),
    });
    setResumeFile(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.candidateName.trim() || !form.candidateEmail.trim() || !form.position.trim() || !form.scheduledAt) {
      setError('Candidate name, email, position and date/time are required.');
      return;
    }
    setSaving(true);
    setError(null);
    setWarning(null);
    try {
      let interviewId = editingId;
      if (editingId == null) {
        const created = await interviewScheduleService.create({
          candidateName: form.candidateName.trim(),
          candidateEmail: form.candidateEmail.trim(),
          candidatePhone: form.candidatePhone.trim() || undefined,
          position: form.position.trim(),
          hiringType: form.hiringType,
          scheduledAt: form.scheduledAt,
          durationMinutes: form.durationMinutes,
          notes: form.notes.trim() || undefined,
          panelUserIds: form.panel.map(p => p.id),
        });
        interviewId = created.id;
        if (created.inviteEmailSent) {
          setSuccess('Interview scheduled — the candidate has been emailed the join link.');
        } else {
          setWarning('Interview scheduled, but the invite email could not be sent — check Admin > Email Settings, then use "Resend Invite" from the list.');
        }
      } else {
        const updated = await interviewScheduleService.reschedule(editingId, {
          scheduledAt: form.scheduledAt,
          durationMinutes: form.durationMinutes,
          notes: form.notes.trim() || undefined,
          panelUserIds: form.panel.map(p => p.id),
        });
        if (updated.inviteEmailSent) {
          setSuccess('Interview rescheduled — the candidate has been emailed the new time.');
        } else {
          setWarning('Interview rescheduled, but the invite email could not be sent — check Admin > Email Settings, then use "Resend Invite" from the list.');
        }
      }

      if (resumeFile && interviewId != null) {
        await interviewScheduleService.uploadResume(interviewId, resumeFile);
      }

      setFormOpen(false);
      await load();
    } catch {
      setError('Could not save the interview. Please check the details and try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await interviewScheduleService.cancel(cancelTarget.id, cancelReason.trim() || undefined);
      setSuccess('Interview cancelled and the candidate has been notified.');
      setCancelTarget(null);
      setCancelReason('');
      await load();
    } catch {
      setError('Could not cancel the interview.');
    }
  };

  const copyLink = (url: string) => {
    navigator.clipboard?.writeText(url).then(() => setSuccess('Join link copied.'));
  };

  const handleResendInvite = async (row: InterviewRow) => {
    setResendingId(row.id);
    setError(null);
    setWarning(null);
    try {
      const updated = await interviewScheduleService.resendInvite(row.id);
      if (updated.inviteEmailSent) {
        setSuccess('Invite email sent.');
      } else {
        setWarning('Still could not send the invite email — check Admin > Email Settings (host, port, credentials) and try again.');
      }
      await load();
    } catch {
      setError('Could not resend the invite email. Please try again.');
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Typography variant="h5">Interview Scheduling</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>Schedule Interview</Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {warning && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setWarning(null)}>{warning}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <TextField
        size="small"
        placeholder="Search by candidate, position or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, mr: 2, width: 360, maxWidth: '100%' }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
      />
      <TextField
        select size="small" label="Hiring type" value={hiringFilter}
        onChange={(e) => setHiringFilter(e.target.value)}
        sx={{ mb: 2, minWidth: 180 }}
      >
        <MenuItem value="">All</MenuItem>
        {HIRING_TYPES.map(t => <MenuItem key={t} value={t}>{EMPLOYMENT_TYPE_META[t].label}</MenuItem>)}
      </TextField>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Candidate</TableCell>
              <TableCell>Position</TableCell>
              <TableCell>Hiring type</TableCell>
              <TableCell>Date &amp; Time</TableCell>
              <TableCell>Panel</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && pagedRows.length === 0 && (
              <TableRow><TableCell colSpan={7}><Typography color="text.secondary" sx={{ py: 2 }} align="center">No interviews scheduled yet.</Typography></TableCell></TableRow>
            )}
            {pagedRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>{row.candidateName}</Typography>
                  <Typography variant="caption" color="text.secondary">{row.candidateEmail}</Typography>
                </TableCell>
                <TableCell>{row.position}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" label={EMPLOYMENT_TYPE_META[row.hiringType]?.label ?? row.hiringType}
                    color={EMPLOYMENT_TYPE_META[row.hiringType]?.color ?? 'default'} />
                </TableCell>
                <TableCell>{formatIst(row.scheduledAt)}</TableCell>
                <TableCell>
                  {row.panelMembers.length === 0
                    ? <Typography variant="caption" color="text.secondary">No panel assigned</Typography>
                    : row.panelMembers.map(p => p.fullName).join(', ')}
                </TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Chip size="small" label={displayStatus(row)} color={displayStatusColor(row)} />
                    {row.status === 'Scheduled' && !row.inviteEmailSent && (
                      <Tooltip title="The candidate has not been emailed the join link yet — the invite failed to send (check Admin > Email Settings) or hasn't been retried.">
                        <WarningAmber fontSize="small" color="warning" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Copy candidate join link">
                    <span>
                      <IconButton size="small" onClick={() => copyLink(row.candidateJoinUrl)} disabled={row.status !== 'Scheduled'}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {row.status === 'Scheduled' && !row.inviteEmailSent && (
                    <Tooltip title="Resend invite email">
                      <span>
                        <IconButton size="small" color="warning" onClick={() => handleResendInvite(row)} disabled={resendingId === row.id}>
                          <Send fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Reschedule">
                    <span>
                      <IconButton size="small" onClick={() => openReschedule(row)} disabled={row.status === 'Cancelled'}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Cancel interview">
                    <span>
                      <IconButton size="small" color="error" onClick={() => setCancelTarget(row)} disabled={row.status !== 'Scheduled'}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination {...paginationProps} />
      </TableContainer>

      {/* Schedule / Reschedule dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId == null ? 'Schedule Interview' : 'Reschedule Interview'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Candidate Name" fullWidth required value={form.candidateName}
              disabled={editingId != null}
              onChange={(e) => setForm({ ...form, candidateName: e.target.value })}
            />
            <TextField
              label="Candidate Email" type="email" fullWidth required value={form.candidateEmail}
              disabled={editingId != null}
              onChange={(e) => setForm({ ...form, candidateEmail: e.target.value })}
            />
            <TextField
              label="Candidate Phone" fullWidth value={form.candidatePhone}
              disabled={editingId != null}
              onChange={(e) => setForm({ ...form, candidatePhone: e.target.value })}
            />
            <TextField
              label="Position" fullWidth required value={form.position}
              disabled={editingId != null}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
            <TextField
              select label="Hiring type" fullWidth required value={form.hiringType}
              disabled={editingId != null}
              onChange={(e) => setForm({ ...form, hiringType: e.target.value })}
              helperText={form.hiringType === 'Intern' ? 'The invite email will mention this is an internship.' : undefined}
            >
              {HIRING_TYPES.map(t => <MenuItem key={t} value={t}>{EMPLOYMENT_TYPE_META[t].label}</MenuItem>)}
            </TextField>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Date & Time" type="datetime-local" fullWidth required
                InputLabelProps={{ shrink: true }}
                value={form.scheduledAt}
                onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
              />
              <TextField
                label="Duration (min)" type="number" sx={{ width: 160 }}
                value={form.durationMinutes}
                onChange={(e) => setForm({ ...form, durationMinutes: Number(e.target.value) || 30 })}
              />
            </Stack>
            <Autocomplete
              multiple
              options={employees}
              getOptionLabel={(o) => `${o.fullName}${o.role ? ` (${o.role})` : ''}`}
              value={form.panel}
              onChange={(_e, value) => setForm({ ...form, panel: value })}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(params) => <TextField {...params} label="Interview Panel" placeholder="Select panel members" />}
            />
            <TextField
              label="Notes" fullWidth multiline minRows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
            <Button component="label" variant="outlined" startIcon={<UploadFile />}>
              {resumeFile ? resumeFile.name : 'Upload Resume'}
              <input type="file" hidden accept=".pdf,.doc,.docx" onChange={(e) => setResumeFile(e.target.files?.[0] || null)} />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editingId == null ? 'Schedule & Notify Candidate' : 'Save & Notify Candidate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <Dialog open={!!cancelTarget} onClose={() => setCancelTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cancel Interview</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Cancel the interview with <strong>{cancelTarget?.candidateName}</strong>? The candidate will be emailed.
          </Typography>
          <TextField
            label="Reason (optional)" fullWidth multiline minRows={2}
            value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelTarget(null)}>Back</Button>
          <Button color="error" variant="contained" onClick={handleCancelConfirm}>Cancel Interview</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterviewScheduling;
